import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell, TableBody, IconButton, TextField, Select, MenuItem, FormControl, InputLabel, Alert, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { API_URL } from '../utils/apiConfig';
import RequirementsBoard from './RequirementsBoard';
import { AuthContext } from '../context/AuthContext';

const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const ROLES = ['Manager', 'Shift Lead', 'Barista', 'Server', 'Kitchen', 'Bartender / Server', 'Barback', 'Cashier'];

export default function StaffingRequirements() {
  const { user } = useContext(AuthContext) || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [effectiveOn, setEffectiveOn] = useState(() => {
    try {
      const saved = localStorage.getItem('dhr_req_effective_on');
      if (saved) return String(saved).slice(0,10);
    } catch (_) {}
    const d = new Date();
    d.setDate(1); // first day of current month
    return d.toISOString().slice(0,10);
  });

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [view, setView] = useState('board'); // board | table

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/locations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (resp.ok) {
          setLocations(data.data || []);
        }
      } catch (e) {
        console.error('Error fetching locations:', e);
      }
    };
    fetchLocations();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ effectiveOn });
      // Only add locationId filter if a specific location is selected
      if (selectedLocation !== 'all') {
        params.set('locationId', selectedLocation);
      }
      const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to load requirements');
      setRows(data.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedLocation, effectiveOn]);

  const addRow = () => {
    const locId = selectedLocation === 'all' ? (user?.store || 1) : selectedLocation;
    setRows(prev => ([...prev, { __new: true, location_id: locId, role: '', day_of_week: 1, start_time: '09:00', end_time: '17:00', headcount: 1, total_hours: null, effective_from: effectiveOn, effective_to: null, notes: '' }]));
  };

  const saveRow = async (row, index) => {
    try {
      const token = localStorage.getItem('token');
      const url = row.id ? `${API_URL.replace(/\/$/, '')}/vnext/requirements/${row.id}` : `${API_URL.replace(/\/$/, '')}/vnext/requirements`;
      const method = row.id ? 'PATCH' : 'POST';
      const body = JSON.stringify({
        location_id: row.location_id,
        role: row.role,
        day_of_week: Number(row.day_of_week),
        start_time: row.start_time,
        end_time: row.end_time,
        headcount: Number(row.headcount||1),
        total_hours: row.total_hours ? Number(row.total_hours) : null,
        effective_from: row.effective_from || effectiveOn,
        effective_to: row.effective_to || null,
        notes: row.notes || null
      });
      const resp = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to save');
      const copy = [...rows];
      copy[index] = data.data;
      setRows(copy);
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteRow = async (row, index) => {
    if (!row.id) { setRows(prev => prev.filter((_,i) => i!==index)); return; }
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/${row.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to delete');
      }
      setRows(prev => prev.filter((_,i) => i!==index));
    } catch (e) {
      setError(e.message);
    }
  };

  const onChange = (i, field, value) => {
    setRows(prev => prev.map((r,idx) => idx===i ? { ...r, [field]: value } : r));
  };

  const totalWeeklyHours = useMemo(() => {
    // estimate total hours = sum(headcount * shiftHours) across week rows
    const shiftHours = (s,e) => {
      const [sh,sm] = s.split(':').map(Number); const [eh,em] = e.split(':').map(Number);
      let start = sh + (sm||0)/60; let end = eh + (em||0)/60; if (isNaN(start)||isNaN(end)) return 0;
      if (end <= start) end += 24; // overnight
      return end - start;
    };
    return rows.reduce((acc, r) => acc + (Number(r.headcount||1) * shiftHours(r.start_time, r.end_time)), 0).toFixed(1);
  }, [rows]);

  const generateForCurrentWeek = async () => {
    try {
      const token = localStorage.getItem('token');
      // find current schedule / week via schedules endpoint
      const params = new URLSearchParams({ start: effectiveOn, end: effectiveOn });
      if (selectedLocation !== 'all') {
        params.set('locationId', selectedLocation);
      }
      const schedResp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/schedules?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const schedData = await schedResp.json();
      const schedule = (schedData.data || [])[0];
      if (!schedule) throw new Error('No schedule found for this week');
      const genResp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/generate-shifts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ schedule_id: schedule.id })
      });
      const gen = await genResp.json();
      if (!genResp.ok) throw new Error(gen.error || 'Failed to generate shifts');
      alert(`Created ${gen.created} open shifts from requirements.`);
    } catch (e) {
      setError(e.message);
    }
  };

  const featureEnabled = process.env.REACT_APP_SCHED_FEATURES === 'true';

  return (
    <Paper sx={{ p: 2, m: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" value={view} exclusive onChange={(e, v)=>{ if (v) setView(v); }}>
          <ToggleButton value="board">Board</ToggleButton>
          <ToggleButton value="table">Table</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Location</InputLabel>
          <Select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} label="Location">
            <MenuItem value="all">All Locations</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          type="date"
          size="small"
          label="Effective On"
          value={effectiveOn}
          onChange={(e) => {
            const v = e.target.value;
            setEffectiveOn(v);
            try { localStorage.setItem('dhr_req_effective_on', v); } catch (_) {}
          }}
        />
      </Stack>

      {view === 'board' && (
        <RequirementsBoard effectiveFrom={effectiveOn} onSaved={()=>load()} selectedLocation={selectedLocation} />
      )}

      {view === 'table' && (
      <>
      {!featureEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Experimental staffing requirements UI is hidden unless REACT_APP_SCHED_FEATURES=true.
        </Alert>
      )}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Staffing Requirements</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<Add />} onClick={addRow}>
          Add Requirement
        </Button>
        <Button
          variant="outlined"
          onClick={async () => {
            if (!window.confirm('Load company template for this location and effective date?')) return;
            try {
              const token = localStorage.getItem('token');
              const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/apply-template`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ location_id: selectedLocation === 'all' ? (user?.store || 1) : selectedLocation, effective_from: effectiveOn, template: 'default', clearExisting: false })
              });
              const data = await resp.json();
              if (!resp.ok) throw new Error(data.error || 'Failed to apply template');
              await load();
            } catch (e) {
              setError(e.message);
            }
          }}
        >
          Load Company Template
        </Button>
        <Button variant="contained" onClick={generateForCurrentWeek}>Generate Week From Requirements</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Estimated total weekly hours: {totalWeeklyHours}h
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Day</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>End</TableCell>
            <TableCell>Headcount</TableCell>
            <TableCell>Total Hours (opt)</TableCell>
            <TableCell>Effective From</TableCell>
            <TableCell>Effective To</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id || `new-${i}`}>
              <TableCell>
                <Select size="small" value={r.day_of_week}
                  onChange={(e)=>onChange(i,'day_of_week', e.target.value)} sx={{ minWidth: 90 }}>
                  {DOW.map((d, idx)=>(<MenuItem key={idx} value={idx}>{d}</MenuItem>))}
                </Select>
              </TableCell>
              <TableCell>
                <Select size="small" value={r.role} onChange={(e)=>onChange(i,'role', e.target.value)} sx={{ minWidth: 160 }}>
                  {ROLES.map(rn=> (<MenuItem key={rn} value={rn}>{rn}</MenuItem>))}
                </Select>
              </TableCell>
              <TableCell>
                <TextField size="small" value={r.start_time} onChange={(e)=>onChange(i,'start_time', e.target.value)} placeholder="HH:MM" />
              </TableCell>
              <TableCell>
                <TextField size="small" value={r.end_time} onChange={(e)=>onChange(i,'end_time', e.target.value)} placeholder="HH:MM" />
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" value={r.headcount} onChange={(e)=>onChange(i,'headcount', e.target.value)} sx={{ width: 90 }} />
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" value={r.total_hours || ''} onChange={(e)=>onChange(i,'total_hours', e.target.value)} sx={{ width: 110 }} />
              </TableCell>
              <TableCell>
                <TextField size="small" type="date" value={r.effective_from || ''} onChange={(e)=>onChange(i,'effective_from', e.target.value)} />
              </TableCell>
              <TableCell>
                <TextField size="small" type="date" value={r.effective_to || ''} onChange={(e)=>onChange(i,'effective_to', e.target.value)} />
              </TableCell>
              <TableCell>
                <TextField size="small" value={r.notes || ''} onChange={(e)=>onChange(i,'notes', e.target.value)} />
              </TableCell>
              <TableCell align="right">
                <IconButton color="primary" onClick={()=>saveRow(r,i)}><Save/></IconButton>
                <IconButton color="error" onClick={()=>deleteRow(r,i)}><Delete/></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {(!rows || rows.length===0) && !loading && (
            <TableRow>
              <TableCell colSpan={10}>
                <Typography variant="body2" color="text.secondary">No requirements yet. Click "Add Requirement" to get started.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </>
      )}
    </Paper>
  );
}
